import type { AxiosInstance } from 'axios';
import qs from './utils/query-string';
import type { Projects } from './interfaces';
import type { DownloadInsightsSummary, ExportProjectTaggedMoments } from './interfaces/projects';
import { transformConferenceTagSummaryItem, transformDates, transformTagSummary } from './transformers';
import { ProjectClips } from '../types/project.clips';

export * as entities from './projects.entities';

export class ProjectsClient {
  constructor(private authenticatedClient: AxiosInstance) { }

  fetchProjects = (data: Projects.FetchProjects.Request) => {
    return this.authenticatedClient.get<Projects.FetchProjects.Response>('/projects', {
      params: data,
    })
      .then(res => res.data);
  };

  fetchProject = (data: Projects.FetchProject.Request) => {
    return this.authenticatedClient.get<Projects.FetchProject.Response>(`/projects/${data.projectId}`)
      .then(res => res.data);
  };

  fetchProjectFiles = (data: Projects.FetchProjectFiles.Request) => {
    return this.authenticatedClient.get<Projects.FetchProjectFiles.Response>(`/projects/${data.projectId}/files`, { transformResponse: transformDates })
      .then(res => res.data);
  };

  fetchProjectTags = (data: Projects.FetchProjectTags.Request) => {
    return this.authenticatedClient.get<Projects.FetchProjectTags.Response>(`/projects/${data.projectId}/tags`)
      .then(res => ({
        tags: res.data.tags,
      }));
  };

  updateProjectTags = (data: Projects.UpdateProjectTags.Request) => {
    return this.authenticatedClient.put<Projects.UpdateProjectTags.Response>(`/projects/${data.projectId}/tags`, {
      ...data,
    })
      .then(res => ({
        items: res.data.items,
      }));
  };

  fetchProjectTerms = (data: Projects.FetchProjectTerms.Request) => {
    return this.authenticatedClient.get<Projects.FetchProjectTerms.Response>(`/projects/${data.projectId}/terms`)
      .then(res => ({
        terms: res.data.terms,
      }));
  };

  fetchProjectTermHighlights = (data: Projects.FetchProjectTermHighlights.Request) => {
    return this.authenticatedClient.get<Projects.FetchProjectTermHighlights.Response>(`/projects/${data.projectId}/terms/highlights`, {
      params: {
        term: data.term,
        searchAfter: data.searchAfter,
      },
    })
      .then(res => ({
        highlights: res.data.highlights,
        searchAfter: res.data.searchAfter,
        total: res.data.total,
      }));
  };

  updateProjectDetails = ({ projectId, ...data }: Projects.UpdateProjectDetails.Request) => {
    return this.authenticatedClient.patch<Projects.UpdateProjectDetails.Response>(`/projects/${projectId}`, {
      ...data,
    })
      .then(res => ({
        project: res.data.project,
      }));
  };

  fetchProjectNoteItems = (data: Projects.FetchProjectNoteItems.Request) => {
    return this.authenticatedClient.get<Projects.FetchProjectNoteItems.Response>(`/projects/${data.projectId}/notes`)
      .then(res => ({
        items: res.data.items,
      }));
  };

  updateProjectNoteItems = (data: Projects.UpdateProjectNoteItems.Request) => {
    return this.authenticatedClient.put<Projects.UpdateProjectNoteItems.Response>(`/projects/${data.projectId}/notes`, {
      ...data,
    })
      .then(res => ({
        items: res.data.items,
      }));
  };

  updateProjectTag = (data: Projects.UpdateProjectTag.Request) => {
    return this.authenticatedClient.patch<Projects.UpdateProjectTag.Response>(`/projects/${data.projectId}/tags/${data.tagId}`, data.updates)
      .then(res => ({
        tag: res.data.tag,
      }));
  };

  downloadInsightsSummary = (data: DownloadInsightsSummary.Request) => {
    const params = {
      export: true,
      format: data.format || `pptx`,
    };

    return this.authenticatedClient.get<Blob>(`/projects/${data.projectId}/insights/export?${qs.stringify(params)}`, { responseType: 'blob' })
      .then<DownloadInsightsSummary.Response>(res => ({
        file: res.data,
        name: `Early Insights Summary.${params.format}`,
      }));
  };

  exportProjectTaggedMoments = (data: ExportProjectTaggedMoments.Request) => {
    return this.authenticatedClient.get<ExportProjectTaggedMoments.Response>(`/projects/${data.projectId}/conferences/tagged-moments/export`);
  };

  deleteProjectTag = (data: Projects.DeleteProjectTag.Request) => {
    return this.authenticatedClient.delete<Projects.DeleteProjectTag.Response>(`/projects/${data.projectId}/tags/${data.tagId}`)
      .then(res => ({
        rebalancedOrdinals: res.data.rebalancedOrdinals,
      }));
  };

  createProjectTag = ({ projectId, ...data }: Projects.CreateProjectTag.Request) => {
    return this.authenticatedClient.post<Projects.CreateProjectTag.Response>(`/projects/${projectId}/tags`, data)
      .then(res => ({
        tag: res.data.tag,
        rebalancedOrdinals: res.data.rebalancedOrdinals,
      }));
  };

  fetchProjectClips = (data: FetchProjectClips.Request) => {
    return this.authenticatedClient.get<FetchProjectClips.Response>(`/projects/${data.projectId}/clips`)
      .then(res => res.data);
  };

  getProjectTranscriptGroupTags = (data: GetProjectTranscriptGroupTags.Request) => {
    return this.authenticatedClient.get<GetProjectTranscriptGroupTags.Response>(`/projects/${data.projectId}/transcript-group-tags`)
      .then(res => res.data);
  };

  getProjectTranscripts = (data: GetProjectTranscripts.Request) => {
    return this.authenticatedClient.get<GetProjectTranscripts.Response>(`/projects/${data.projectId}/transcripts`)
      .then(res => res.data);
  };

  getTagSummaries = (data: Projects.GetProjectConferenceTagSummaries.Request) => {
    return this.authenticatedClient.get<Projects.GetProjectConferenceTagSummaries.Response<string>>(`/projects/${data.projectId}/conferences/tags/summaries`)
      .then(res => {
        return {
          ...res.data,
          items: res.data.items.map(transformConferenceTagSummaryItem),
        };
      });
  };


  getTagSummaryCitations = ({ projectId, tagSummaryId }: Projects.GetTagSummaryCitations.Request) => {
    return this.authenticatedClient.get<Projects.GetTagSummaryCitations.Response>(`/projects/${projectId}/conferences/tags/summaries/${tagSummaryId}/citations`)
      .then(res => res.data);
  };

  getTagSummaryVersions = ({ projectId, tagSummaryId }: Projects.GetTagSummaryVersions.Request) => {
    return this.authenticatedClient.get<Projects.GetTagSummaryVersions.Response<string>>(`/projects/${projectId}/conferences/tags/summaries/${tagSummaryId}/versions`)
      .then(res => {
        return {
          ...res.data,
          items: res.data.items.map(transformTagSummary),
        };
      });
  };

  generateTagSummaries = ({ projectId, ...data }: Projects.GenerateProjectConferenceTagSummaries.Request) => {
    return this.authenticatedClient.post<Projects.GenerateProjectConferenceTagSummaries.Response>(`/projects/${projectId}/conferences/tags/summaries/generate`, {
      tagIds: data.tagIds,
    });
  };

  fetchAllSurveyResponses = ({ projectId, ...query }: Projects.FetchMultiSurveyResponses.Request) => {
    return this.authenticatedClient.get<Projects.FetchMultiSurveyResponses.Response>(`/projects/${projectId}/surveys/all-responses?${qs.stringify(query)}`)
      .then(res => res.data);
  };

  fetchProjectSurveys = (data: Projects.FetchProjectSurveys.Request) => {
    return this.authenticatedClient.get<Projects.FetchProjectSurveys.Response>(`/projects/${data.projectId}/surveys`)
      .then(res => res.data);
  };

}

namespace FetchProjectClips {
  export type Request = {
    tagIds?: number[];
  } & IProjectId;

  export type Response = {
    items: ProjectClips.Item[];
    total: number;
  };
}

namespace GetProjectTranscriptGroupTags {
  export type Request = IProjectId;

  export type Response = {
    tags: {
      id: number;
      name: string;
    }[];
  };
}

namespace GetProjectTranscripts {
  export type Request = IProjectId;

  export type Response = {
    transcripts: {
      id: number;
      name: string;
    }[];
  };
}